import { createContext, RefObject } from 'react';
import { Canvas } from 'fabric';
import { CreativityType } from '../../../types/banner';
import type { FormField, RoutedFormData } from '../../../types/templates';
import type { FieldChangeHandler } from '../../../types/form-renderer';
import type {
  CanvasBinding,
  CanvasJSON,
  CreativityEditorHandle,
  LayerLabelResolver,
  ResolutionConfig
} from '@doer/frontend-components/creativity-editor';

export type EditorDataContextValue = {
  // form
  formData: RoutedFormData;
  errors: Record<string, string>;
  fields: readonly FormField[];
  isFormComplete: boolean;
  handleFieldChange: FieldChangeHandler;
  setFieldError: (fieldKey: string, error?: string) => void;
  validateForm: () => { isValid: boolean; errors: Record<string, string> };

  // template
  creativityType: CreativityType;
  canvasBinding: CanvasBinding;
  // Set when reopening a saved piece; save updates this id instead of creating a new one.
  contentId?: string;

  // resolutions (controlled in <CreativityEditor>)
  resolutions: ResolutionConfig[];
  activeResolution: ResolutionConfig;
  setActiveResolutionId: (id: string) => void;

  // editor wiring
  editorRef: RefObject<CreativityEditorHandle | null>;
  editorReady: boolean;
  contentReady: boolean;
  canvas: Canvas | null;
  onEditorReady: () => void;
  onLoadingChange: (loading: boolean) => void;
  layerLabelResolver: LayerLabelResolver;

  /**
   * Returns the best available JSON for a given resolution:
   * - Active resolution → serializes the live canvas (captures latest edits)
   * - Other resolutions → materializes JSON in an off-screen StaticCanvas running
   *   applyAllBindings end-to-end. Async because effects (image setSrc, arrangeGroup)
   *   need runtime asset dimensions that only exist after load completes.
   */
  resolveResolutionJson: (resolution: ResolutionConfig) => Promise<CanvasJSON>;

  // snapshots
  saveResolutionSnapshot: (resolutionId: string) => void;
  getResolutionSnapshot: (resolutionId: string) => CanvasJSON | undefined;

  syncObjectAcrossResolutions: (pieceId: string, props: Record<string, unknown>) => void;
};

export const EditorDataContext = createContext<EditorDataContextValue | null>(null);
